import { holdsAny, STAFF_DASHBOARD_AREAS } from '@brickup/shared/constants'
import { NAV } from '@brickup/shared/locales/de/nav'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { CatalogueDashboard } from './CatalogueDashboard'
import { ProjectsDashboard } from './ProjectsDashboard'
import { SalesDashboard } from './SalesDashboard'
import { SystemDashboard } from './SystemDashboard'
import { PageLayout } from '@/components/Internal/PageLayout'
import { selectUserRoles, useAppStore } from '@/stores'

/** One tab per area, open to the roles the area's endpoint admits. */
const TABS = [
  {
    id: 'sales',
    label: STAFF_DASHBOARD.tabs.sales,
    roles: STAFF_DASHBOARD_AREAS.sales,
    Body: SalesDashboard,
  },
  {
    id: 'projects',
    label: STAFF_DASHBOARD.tabs.projects,
    roles: STAFF_DASHBOARD_AREAS.projects,
    Body: ProjectsDashboard,
  },
  {
    id: 'catalogue',
    label: STAFF_DASHBOARD.tabs.catalogue,
    roles: STAFF_DASHBOARD_AREAS.catalogue,
    Body: CatalogueDashboard,
  },
  {
    id: 'system',
    label: STAFF_DASHBOARD.tabs.system,
    roles: STAFF_DASHBOARD_AREAS.system,
    Body: SystemDashboard,
  },
] as const

export type DashboardTab = (typeof TABS)[number]['id']
export const DASHBOARD_TABS: readonly DashboardTab[] = TABS.map(tab => tab.id)

/**
 * The staff dashboard: the tabs the reader's roles open, the first of them
 * where the URL names none or one the reader may not open. An account with a
 * single area gets no tab strip — a lone tab says nothing.
 */
export function StaffDashboard({
  tab,
  onTabChange,
}: {
  tab: DashboardTab | undefined
  onTabChange: (tab: DashboardTab) => void
}) {
  const roles = useAppStore(selectUserRoles)
  const open = TABS.filter(entry => holdsAny(roles, entry.roles))
  const active = open.find(entry => entry.id === tab) ?? open[0]

  return (
    <PageLayout
      title={NAV.dashboard}
      tabs={
        open.length > 1
          ? open.map(entry => ({ id: entry.id, label: entry.label }))
          : undefined
      }
      activeTab={active?.id}
      onTabChange={id => onTabChange(id as DashboardTab)}
    >
      {active ? <active.Body /> : null}
    </PageLayout>
  )
}
